import { useEffect, useRef, useState, type PointerEvent, type RefObject } from 'react';

import { publishBoardSocketPackage } from '@walls/services/boardWebsockets';
import { wallsApi } from '@walls/services/wallsApi';
import {
  useCardImageAspectRatioStore,
  useBoardInteractionStore,
  useWallContentStore,
  useWallStore,
  useWorkspaceUiStore,
  type WallBoard,
  type WallCard,
} from '@walls/store';
import { getCardRect } from '../../positioning/cardRects';
import { positionRelativeToResize } from '../../positioning/autoAlign';
import { isClusterCard } from '../../selectors/boardCardSelectors';
import { getResizedCardSize, type CardResizeHandle } from '../utils/cardResizeGeometry';
import { isImageCard } from '../utils/cardDisplay';

interface UseCardResizeOptions {
  board: WallBoard;
  canResize: boolean;
  card: WallCard;
  elementRef: RefObject<HTMLElement | null>;
  isClusterChild: boolean;
}

interface ResizeState {
  handle: CardResizeHandle;
  pointerStartX: number;
  pointerStartY: number;
  previousRect: { height: number; width: number; x: number; y: number };
  startSize: { height: number; width: number };
}

export function useCardResize({
  board,
  canResize,
  card,
  elementRef,
  isClusterChild,
}: UseCardResizeOptions) {
  const resizeState = useRef<ResizeState | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const cards = useWallContentStore((state) => state.cards);
  const updateCard = useWallContentStore((state) => state.updateCard);
  const setResizing = useBoardInteractionStore((state) => state.setResizing);
  const topicKey = useWallStore((state) => state.topicKey);
  const selectedTool = useWorkspaceUiStore((state) => state.selectedTool);
  const zoom = useWorkspaceUiStore((state) => state.zoom);
  const storedImageAspectRatio = useCardImageAspectRatioStore((state) => state.ratios[card.id]);
  const canUseResize =
    canResize &&
    !isClusterChild &&
    !card.isLocked &&
    !['hand', 'shape', 'line', 'text', 'cluster'].includes(selectedTool);

  useEffect(
    () => () => {
      if (resizeState.current) {
        setResizing(card.id, false);
      }
    },
    [card.id, setResizing]
  );

  return {
    isResizing,
    canResize: canUseResize,
    getHandleProps: (handle: CardResizeHandle) => ({
      onPointerDown: (event: PointerEvent<HTMLElement>) => {
        if (!canUseResize || event.button !== 0) {
          return;
        }

        const element = elementRef.current;
        const cardRect = getCardRect(card);

        if (!element || !cardRect) {
          return;
        }

        const elementRect = element.getBoundingClientRect();
        const previousRect = {
          ...cardRect,
          height: elementRect.height / zoom,
          width: elementRect.width / zoom,
        };

        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        resizeState.current = {
          handle,
          pointerStartX: event.clientX,
          pointerStartY: event.clientY,
          previousRect,
          startSize: {
            height: previousRect.height,
            width: previousRect.width,
          },
        };
        setResizing(card.id, true);
        setIsResizing(true);
      },
      onPointerMove: (event: PointerEvent<HTMLElement>) => {
        const currentResize = resizeState.current;

        if (!currentResize || !canUseResize) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const nextSize = getResizedCardSize({
          aspectRatio: getImageAspectRatio(card, currentResize.startSize, storedImageAspectRatio),
          card,
          deltaX: (event.clientX - currentResize.pointerStartX) / zoom,
          deltaY: (event.clientY - currentResize.pointerStartY) / zoom,
          handle: currentResize.handle,
          startSize: currentResize.startSize,
        });

        updateCard(card.id, nextSize);
      },
      onPointerUp: (event: PointerEvent<HTMLElement>) => {
        const currentResize = resizeState.current;
        resizeState.current = null;

        if (!currentResize) {
          return;
        }

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }

        event.preventDefault();
        event.stopPropagation();
        setResizing(card.id, false);
        setIsResizing(false);

        const nextSize = getResizedCardSize({
          aspectRatio: getImageAspectRatio(card, currentResize.startSize, storedImageAspectRatio),
          card,
          deltaX: (event.clientX - currentResize.pointerStartX) / zoom,
          deltaY: (event.clientY - currentResize.pointerStartY) / zoom,
          handle: currentResize.handle,
          startSize: currentResize.startSize,
        });
        const resizedCard = { ...card, ...nextSize };
        const projectedCards = cards.map((currentCard) =>
          currentCard.id === resizedCard.id ? resizedCard : currentCard
        );
        const positionUpdates = board.autoAlignCards
          ? positionRelativeToResize(projectedCards, currentResize.previousRect, resizedCard)
          : [];

        updateCard(card.id, nextSize);
        void wallsApi.updateCardSize(
          board.id,
          card.id,
          { id: card.id, height: nextSize.height, width: nextSize.width },
          topicKey
        );
        publishBoardSocketPackage({
          action: isClusterCard(card) ? 'updateClusterSize' : 'updateCardSize',
          topic: board.id,
          size: { id: card.id, height: nextSize.height, width: nextSize.width },
        });

        positionUpdates.forEach(({ cardId, xPos, yPos }) => {
          updateCard(cardId, { xPos, yPos });
          void wallsApi.updateCardPosition(board.id, cardId, { id: cardId, xPos, yPos }, topicKey);
          publishBoardSocketPackage({
            action: 'updateCardPosition',
            topic: board.id,
            position: { id: cardId, xPos, yPos },
          });
        });
      },
      onPointerCancel: (event: PointerEvent<HTMLElement>) => {
        const currentResize = resizeState.current;
        resizeState.current = null;

        if (!currentResize) {
          return;
        }

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }

        setResizing(card.id, false);
        setIsResizing(false);
        updateCard(card.id, currentResize.startSize);
      },
    }),
  };
}

function getImageAspectRatio(
  card: WallCard,
  startSize: { height: number; width: number },
  storedImageAspectRatio?: number
) {
  if (!isImageCard(card)) {
    return null;
  }

  if (
    storedImageAspectRatio &&
    Number.isFinite(storedImageAspectRatio) &&
    storedImageAspectRatio > 0
  ) {
    return storedImageAspectRatio;
  }

  if (startSize.height > 0 && startSize.width > 0) {
    return startSize.width / startSize.height;
  }

  return null;
}
